import { Card, CardContent, CardMedia, CircularProgress, Grid } from '@mui/material';
import moment from 'moment';
import React, { useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useGetWorkerContracts, useGetWorkerDocuments } from 'src/api/hooks/useWorker';
import { DocumentType } from 'src/DTO/Document.type';
import { ORG_TYPE } from 'src/DTO/OrganizationType.enum';
import { useStoreActions, useStoreState } from 'src/stores';
import { PopUpEnum } from 'src/stores/NotificationStore';
import { Box } from '../Box';
import { Button } from '../Button';
import { BUTTON_COLOR, BUTTON_VARIANT } from '../Button/types';
import { Documents } from '../Documents/Documents';
import { Typography } from '../Typography';
import { TYPOGRAPHY_COLORS } from '../Typography/types';

export const WorkerDocuments: React.FC = () => {
  const { t } = useTranslation();
  const setPopUpData = useStoreActions(action => action.popUp.setPopUp);
  const { id: workerId } = useParams<{ id: string }>();
  const isCompany =
    useStoreState(state => state.user.data.companies?.[0]?.organizationType) === ORG_TYPE.COMPANY;

  const [getDocuments, { loading: getDocumentsLoading, data: documents }] = useGetWorkerDocuments({
    onError: () =>
      setPopUpData({
        isOpen: true,
        type: PopUpEnum.error,
        message: t('workerProfile.documents.documents.error'),
      }),
  });

  const [getContracts, { loading: getContractsLoading, data: contractsResponse }] =
    useGetWorkerContracts({
      onError: () =>
        setPopUpData({
          isOpen: true,
          type: PopUpEnum.error,
          message: t('workerProfile.documents.contracts.error'),
        }),
    });
  const contracts = contractsResponse?.workerContracts;

  useEffect(() => {
    getDocuments({
      variables: {
        id: workerId,
      },
    });

    if (!isCompany) return;

    getContracts({
      variables: {
        id: workerId,
      },
    });
  }, [workerId, isCompany]);

  const isLoading = getDocumentsLoading || getContractsLoading;

  return (
    <Grid container spacing={4} style={{ padding: '20px' }}>
      {isLoading && (
        <Grid item xs={12} display="flex" justifyContent="center" alignItems="center">
          <CircularProgress color="secondary" />
        </Grid>
      )}

      {!isLoading && !contracts?.length && !documents?.length && (
        <Grid item xs={12} display="flex" justifyContent="center" alignItems="center">
          <Typography>
            <Trans>workerProfile.documents.noContent</Trans>
          </Typography>
        </Grid>
      )}

      {contracts && contracts.length > 0 && (
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            <Trans>workerProfile.documents.contracts.title</Trans>
            {':'}
          </Typography>
          <Grid container spacing={2}>
            {contracts.map(contract => (
              <Grid item xs={12} sm={6} lg={4} key={contract.url} display="flex">
                <Card variant="outlined" sx={{ display: 'flex', width: '100%' }}>
                  <CardContent
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      flex: 1,
                    }}>
                    <Box>
                      <Typography
                        variant="subtitle1"
                        gutterBottom
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}>
                        {contract.title}
                      </Typography>
                      <Typography variant="body2" color={TYPOGRAPHY_COLORS.SECONDARY}>
                        <Trans>workerProfile.documents.generationDate</Trans>
                        {': '}
                        {moment(contract.createdAt).format('YYYY-MM-DD HH:mm')}
                      </Typography>
                    </Box>

                    <Button
                      variant={BUTTON_VARIANT.GRADIENT}
                      color={BUTTON_COLOR.DARK}
                      href={contract.url}
                      target="_blank"
                      sx={{ mt: 2 }}>
                      <Trans>workerProfile.documents.view</Trans>
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Grid>
      )}

      {documents && documents.length > 0 && (
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            <Trans>workerProfile.documents.documents.title</Trans>
            {':'}
          </Typography>
          <Grid container spacing={2}>
            {documents.map((document, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card variant="outlined">
                  <CardMedia>
                    <Typography>
                      {document.type === DocumentType.StudentId ? (
                        <Trans>workerProfile.documents.studentId</Trans>
                      ) : document.type === DocumentType.StudentStatusVerification ? (
                        <Trans>workerProfile.documents.studentStatus</Trans>
                      ) : (
                        <Trans>workerProfile.documents.doctorsPaper</Trans>
                      )}
                    </Typography>
                    <Documents size={150} imageUrls={document.documentUrl} />
                  </CardMedia>
                  <CardContent>
                    <Typography variant="body2" color={TYPOGRAPHY_COLORS.SECONDARY}>
                      <Trans>workerProfile.documents.uploadDate</Trans>
                      {': '}
                      {moment(document.createdAt).format('YYYY-MM-DD HH:mm')}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};
